import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable, HttpsCallableResult } from '@angular/fire/functions';

import { LoggerService } from '../logger/logger.service';

/**
 * Wrapper service for Firebase Cloud Functions.
 *
 * This abstraction allows for easier testing by providing an injectable
 * service that can be mocked, rather than directly using httpsCallable
 * which is a standalone ES module export.
 *
 * Follows the same pattern as FirestoreService, AuthService, etc.
 */
@Injectable({
  providedIn: 'root',
})
export class FunctionsService {
  private readonly functions = inject(Functions);
  private readonly logger = inject(LoggerService);

  /**
   * Create a callable function reference with automatic logging.
   *
   * @param name The name of the Cloud Function to call
   * @returns A callable function that returns a Promise
   */
  callable<Req, Res>(name: string): (request: Req) => Promise<HttpsCallableResult<Res>> {
    const fn = httpsCallable<Req, Res>(this.functions, name);

    return async (request: Req): Promise<HttpsCallableResult<Res>> => {
      const correlationId = this.logger.callableStart(name, request);
      const startTime = performance.now();

      try {
        const result = await fn(request);
        const duration = Math.round(performance.now() - startTime);
        this.logger.callableEnd(name, correlationId, duration, result.data);
        return result;
      } catch (error) {
        const duration = Math.round(performance.now() - startTime);
        this.logger.callableEnd(name, correlationId, duration, undefined, error as Error);
        throw error;
      }
    };
  }
}
